import React, { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Navbar } from 'react-bootstrap'
import GoBackIcon from '../components/assetsComponents/GoBackIcon'
import {
  ABSpinner,
  FeatherLoader,
  QuestionRenderer,
  useErrorService,
  useUserService,
} from '@abroad/components'
import API from '../utils/API'
// import QuestionRenderer from '../components/form/QuestionRenderer'
import { gtmSubmitExperienceEnrollmentAction } from '../components/quests/gtmQuestUploadFunctions'
import { isQuestPaymentSatisfied } from '../components/quests/questPaymentUtils'
import useResolutionService from '../hooks/useResolutionService'

const EnrollTypeForm = () => {
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const { questId } = useParams()
  const { user } = useUserService()
  const history = useHistory()
  const Error = useErrorService()
  const { isSMScreen, isMDScreen, isLGScreen } = useResolutionService()
  const [questions, setQuestions] = useState([])
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [formData, setFormData] = useState({})
  const [customFormId, setCustomFormId] = useState(null)
  const userId = user?.id
  // Keep a ref in sync so back/next saves never overwrite with stale formData
  const formDataRef = React.useRef(formData)
  formDataRef.current = formData

  // Helper function to get localStorage key
  const getStorageKey = () => {
    return customFormId ? `${customFormId}-${userId}` : null
  }

  // Helper function to save form data to localStorage
  const saveFormDataToStorage = (data) => {
    const storageKey = getStorageKey()
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(data))
    }
  }

  // Helper function to load form data from localStorage
  const loadFormDataFromStorage = () => {
    const storageKey = getStorageKey()
    if (storageKey) {
      const savedData = localStorage.getItem(storageKey)
      if (savedData) {
        return JSON.parse(savedData)
      }
    }
    return {}
  }

  // Helper function to clear form data from localStorage
  const clearFormDataFromStorage = () => {
    const storageKey = getStorageKey()
    if (storageKey) {
      localStorage.removeItem(storageKey)
    }
  }

  // Goal: when admin changes a question's answerType, localStorage may still hold
  // the old answer shape for that same question id. Reusing it breaks the UI.
  // Example: address answer `{ street: "A" }` kept after type becomes text → input
  // shows "[object Object]"; number kept after type becomes date → .split crash.
  // On load we drop answers whose runtime shape no longer matches answerType
  // (compatible drafts are kept; mismatched / deleted-question answers are cleared).
  const isAnswerCompatibleWithType = (answer, answerType) => {
    if (answer === undefined || answer === null) {
      return false
    }

    switch (answerType) {
      case 'object':
        return (
          typeof answer === 'object' &&
          !Array.isArray(answer) &&
          answer !== null
        )
      case 'multipleSelectionDropdown':
        return Array.isArray(answer)
      case 'scale':
        return (
          typeof answer === 'number' ||
          (typeof answer === 'string' &&
            answer.trim() !== '' &&
            !Number.isNaN(Number(answer)))
        )
      case 'text':
      case 'email':
      case 'phone':
      case 'country':
      case 'date':
      case 'singleSelectionDropdown':
      case 'files':
        return typeof answer === 'string'
      default:
        return typeof answer === 'string'
    }
  }

  // Applies isAnswerCompatibleWithType across saved form data, then writes the
  // cleaned map back to localStorage so stale shapes are not reloaded next time.
  const sanitizeFormDataAgainstQuestions = (
    savedData,
    questionsList,
    storageKeyOverride,
  ) => {
    if (!savedData || typeof savedData !== 'object') {
      return {}
    }

    // Avoid wiping storage if called before questions are available
    if (!questionsList || questionsList.length === 0) {
      return savedData
    }

    const questionById = questionsList.reduce((acc, question) => {
      acc[question._id] = question
      return acc
    }, {})

    const sanitized = {}
    Object.keys(savedData).forEach((questionId) => {
      const question = questionById[questionId]
      if (!question) {
        return
      }
      const answer = savedData[questionId]
      if (isAnswerCompatibleWithType(answer, question.answerType)) {
        sanitized[questionId] = answer
      }
    })

    const storageKey = storageKeyOverride || getStorageKey()
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(sanitized))
    }

    return sanitized
  }

  // Helper function to check if a dependent question should be shown
  const shouldShowDependentQuestion = (
    question,
    allQuestions,
    currentFormData,
  ) => {
    if (question.questionType !== 'dependent') {
      return true
    }

    // Find the parent question
    const parentQuestion = allQuestions.find(
      (q) => q._id === question.dependentQuestionId,
    )
    if (!parentQuestion) {
      return false
    }

    // Get the parent question's answer
    const parentAnswer = currentFormData[parentQuestion._id]
    if (!parentAnswer) {
      return false
    }

    // Check if the parent answer matches any of the conditions
    const conditions = question.condition || []
    if (conditions.length === 0) {
      return false
    }

    // Handle different answer types for parent question
    if (parentQuestion.answerType === 'multipleSelectionDropdown') {
      // For multiple selection, check if ANY of the selected options match ANY of the conditions
      if (Array.isArray(parentAnswer)) {
        // Check if any of the user's selected options match any of the conditions
        return parentAnswer.some((selectedOption) => {
          // Find the option ID for the selected option text
          const selectedOptionObj = parentQuestion.options?.find(
            (opt) => opt.option === selectedOption,
          )
          const selectedOptionId =
            selectedOptionObj?._id || selectedOptionObj?.id
          return conditions.some((condition) => selectedOptionId === condition)
        })
      } else if (typeof parentAnswer === 'string') {
        // Handle case where single selection is stored as string
        const selectedOptionObj = parentQuestion.options?.find(
          (opt) => opt.option === parentAnswer,
        )
        const selectedOptionId = selectedOptionObj?._id || selectedOptionObj?.id
        return conditions.some((condition) => selectedOptionId === condition)
      }
      return false
    } else {
      // For single selection, check if the answer matches any of the conditions
      const answerValue = Array.isArray(parentAnswer)
        ? parentAnswer[0]
        : parentAnswer

      // Find the option ID for the selected option text
      const selectedOptionObj = parentQuestion.options?.find(
        (opt) => opt.option === answerValue,
      )
      const selectedOptionId = selectedOptionObj?._id || selectedOptionObj?.id

      return conditions.some((condition) => selectedOptionId === condition)
    }
  }

  // Helper function to filter questions based on dependent logic
  const getFilteredQuestions = (allQuestions, currentFormData) => {
    return allQuestions.filter((question) => {
      // For new users, filter out deleted questions completely
      if (question.isDeleted) {
        return false
      }

      // For non-deleted questions, apply dependent logic
      return shouldShowDependentQuestion(
        question,
        allQuestions,
        currentFormData,
      )
    })
  }

  // Helper function to check if there are more questions after the current one
  const hasMoreQuestionsAfterCurrent = (currentQuestionId, currentFormData) => {
    const currentQuestionIndex = questions.findIndex(
      (q) => q._id === currentQuestionId,
    )

    if (currentQuestionIndex === -1) {
      return false
    }

    // Get all questions after the current one
    const questionsAfterCurrent = questions.slice(currentQuestionIndex + 1)

    // Filter out deleted questions and check dependent logic
    const visibleQuestionsAfterCurrent = questionsAfterCurrent.filter(
      (question) => {
        if (question.isDeleted) {
          return false
        }

        return shouldShowDependentQuestion(question, questions, currentFormData)
      },
    )

    return visibleQuestionsAfterCurrent.length > 0
  }

  const handleSubmit = async (values) => {
    setSubmitting(true)
    try {
      // Include ALL questions (including dependent questions that might not be shown)
      // but filter out deleted questions
      const allNonDeletedQuestions = questions.filter((q) => !q.isDeleted)

      // Transform form data into the required API format - include all questions
      const answers = allNonDeletedQuestions.map((question) => {
        const questionAnswer = values[question._id]

        if (question.answerType === 'object') {
          // For object type questions, use subAnswers structure
          const subAnswers = question.subQuestions.map((subQ) => ({
            subQuestionId: subQ._id,
            answer:
              questionAnswer && questionAnswer[subQ._id]
                ? [questionAnswer[subQ._id]]
                : [],
          }))

          return {
            questionId: question._id,
            subAnswers: subAnswers,
          }
        } else {
          // For all other question types, use answer structure
          let formattedAnswer = []

          // Handle different question types appropriately
          if (
            question.answerType === 'singleSelectionDropdown' ||
            question.answerType === 'multipleSelectionDropdown'
          ) {
            // For single and multiple selection questions, always send an array
            if (questionAnswer) {
              if (Array.isArray(questionAnswer)) {
                // For multiple selection questions, keep as array
                formattedAnswer = questionAnswer
              } else if (
                typeof questionAnswer === 'string' &&
                questionAnswer.trim() !== ''
              ) {
                // For single selection questions, wrap in array
                formattedAnswer = [questionAnswer]
              } else if (
                questionAnswer !== null &&
                questionAnswer !== undefined
              ) {
                // For any other non-null values
                formattedAnswer = [questionAnswer.toString()]
              }
            }
            // If no answer provided, formattedAnswer remains empty array []
          } else {
            // For other question types (text, email, phone, date, scale, files)
            if (questionAnswer) {
              if (Array.isArray(questionAnswer)) {
                formattedAnswer = questionAnswer
              } else if (
                typeof questionAnswer === 'string' &&
                questionAnswer.trim() !== ''
              ) {
                formattedAnswer = [questionAnswer]
              } else if (
                questionAnswer !== null &&
                questionAnswer !== undefined
              ) {
                formattedAnswer = [questionAnswer.toString()]
              }
            }
            // If no answer provided, formattedAnswer remains empty array []
          }

          return {
            questionId: question._id,
            answer: formattedAnswer,
          }
        }
      })

      const payload = {
        answers: answers,
      }

      await API.quests.updateCustomFormAnswers(questId, customFormId, payload)

      // Track GTM event for successful enrollment form submission
      gtmSubmitExperienceEnrollmentAction(
        questId,
        customFormId,
        answers.length,
        user?._id,
      )

      // Clear localStorage after successful submission
      clearFormDataFromStorage()

      let redirectPath = `/your-experiences/${questId}/payments`
      try {
        const { data } = await API.quests.getQuestsSteps(questId)
        const questPayments = data?.questPayments || {}
        if (
          isQuestPaymentSatisfied({
            canPayFull: questPayments.canPayFull,
            canPayDeposit: questPayments.canPayDeposit,
            canPayRemaining: questPayments.canPayRemaining,
            depositAmount: questPayments.depositAmount,
            actualCost: questPayments.actualCost,
            discountedCost: questPayments.discountedCost,
          })
        ) {
          redirectPath = `/your-experiences?questId=${questId}`
        }
      } catch (e) {
        // Fall back to payment page if status check fails
      }

      history.push(redirectPath)
    } catch (error) {
      Error.showError(error)
      // Handle error
    } finally {
      setSubmitting(false)
    }
  }

  const handleNext = () => {
    const filteredQuestions = getFilteredQuestions(questions, formData)
    const currentQuestionIndex = filteredQuestions.findIndex(
      (q) => q._id === questions[currentQuestion]._id,
    )

    if (currentQuestionIndex < filteredQuestions.length - 1) {
      // Find the next question in the original questions array
      const nextFilteredQuestion = filteredQuestions[currentQuestionIndex + 1]
      const nextQuestionIndex = questions.findIndex(
        (q) => q._id === nextFilteredQuestion._id,
      )
      setCurrentQuestion(nextQuestionIndex)
    }
  }

  const handlePrevious = () => {
    const filteredQuestions = getFilteredQuestions(questions, formData)
    const currentQuestionIndex = filteredQuestions.findIndex(
      (q) => q._id === questions[currentQuestion]._id,
    )

    if (currentQuestionIndex > 0) {
      // Find the previous question in the original questions array
      const prevFilteredQuestion = filteredQuestions[currentQuestionIndex - 1]
      const prevQuestionIndex = questions.findIndex(
        (q) => q._id === prevFilteredQuestion._id,
      )
      setCurrentQuestion(prevQuestionIndex)
    }
  }

  // Helper function to validate a single question
  const validateQuestion = (question, answer) => {
    if (!question.isRequired) {
      return true
    }

    if (question.answerType === 'object') {
      // For object type, check if all sub-questions have values
      const objectValue = answer || {}
      return question.subQuestions.every((subQ) => {
        const subValue = objectValue[subQ._id]
        return subValue && subValue.trim && subValue.trim() !== ''
      })
    } else if (question.answerType === 'multipleSelectionDropdown') {
      // For multiple selection, check if at least one option is selected
      return Array.isArray(answer) && answer.length > 0
    } else if (question.answerType === 'email') {
      // For email, check if value exists and is valid email format
      if (!answer || answer.toString().trim() === '') {
        return false
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(answer.toString().trim())
    } else if (question.answerType === 'phone') {
      // For phone, check if value exists and is valid phone format
      if (!answer || answer.toString().trim() === '') {
        return false
      }
      const phoneString = answer.toString().trim()
      const digitsOnly = phoneString.replace(/\D/g, '')

      // Basic phone validation: 7-15 digits
      if (digitsOnly.length < 7 || digitsOnly.length > 15) {
        return false
      }

      // Additional validation for common country formats
      if (phoneString.includes('+1')) {
        // US/Canada format
        const usDigits = digitsOnly.replace(/^1/, '') // Remove country code
        return usDigits.length === 10
      }

      return true
    } else if (question.answerType === 'country') {
      // For country, check if value exists and is valid country format
      if (!answer || answer.toString().trim() === '') {
        return false
      }
      const countryString = answer.toString().trim()
      // Check if it matches the expected format: +{dialCode} {countryName}
      const countryMatch = countryString.match(/^\+(\d+)\s+(.+)$/)
      return countryMatch && countryMatch[1] && countryMatch[2]
    } else {
      // For other types, check if the value exists and is not empty
      return answer && answer.toString && answer.toString().trim() !== ''
    }
  }

  // Persist answer for any question (required or optional) without navigating
  const handleSaveAnswer = (questionId, answer) => {
    const newFormData = {
      ...formDataRef.current,
      [questionId]: answer,
    }
    formDataRef.current = newFormData
    setFormData(newFormData)
    saveFormDataToStorage(newFormData)
  }

  const handleQuestionSubmit = (questionId, answer) => {
    const currentQ = questions[currentQuestion]

    // Validate the current question
    if (!validateQuestion(currentQ, answer)) {
      // Don't proceed if required field is invalid
      return
    }

    // Persist answer for required and non-required questions alike
    const newFormData = {
      ...formDataRef.current,
      [questionId]: answer,
    }
    formDataRef.current = newFormData
    setFormData(newFormData)
    saveFormDataToStorage(newFormData)

    // Get filtered questions to determine if there are more questions to show
    const filteredQuestions = getFilteredQuestions(questions, newFormData)
    const currentQuestionIndex = filteredQuestions.findIndex(
      (q) => q._id === questionId,
    )

    // Move to next question if there are more filtered questions
    if (currentQuestionIndex < filteredQuestions.length - 1) {
      // Find the next question in the original questions array
      const nextFilteredQuestion = filteredQuestions[currentQuestionIndex + 1]
      const nextQuestionIndex = questions.findIndex(
        (q) => q._id === nextFilteredQuestion._id,
      )
      setCurrentQuestion(nextQuestionIndex)
    }
  }

  // Handle final submit from question components
  const handleFinalSubmit = async (finalFormData) => {
    // Merge with ref so optional answers saved while going back are not dropped
    const mergedFormData = {
      ...formDataRef.current,
      ...finalFormData,
    }

    // Get filtered questions that are actually shown to the user for validation
    const filteredQuestions = getFilteredQuestions(questions, mergedFormData)

    // Validate only the questions that are shown to the user
    const validationResults = filteredQuestions.map((question) => {
      const answer = mergedFormData[question._id]
      const isValid = validateQuestion(question, answer)

      return {
        questionId: question._id,
        questionText: question.question,
        isRequired: question.isRequired,
        answerType: question.answerType,
        answer: answer,
        isValid: isValid,
      }
    })

    const invalidQuestions = validationResults.filter(
      (result) => !result.isValid,
    )
    const allQuestionsValid = invalidQuestions.length === 0

    if (!allQuestionsValid) {
      // console.error('Validation failed for the following questions:')
      // invalidQuestions.forEach((q) => {
      //   console.error(`- Question: "${q.questionText}" (ID: ${q.questionId})`)
      //   console.error(`  Type: ${q.answerType}, Required: ${q.isRequired}`)
      //   console.error(`  Answer:`, q.answer)
      // })
      return
    }

    // Submit all questions (including dependent questions that might not be shown)
    // but only after validation passes for the questions that are shown
    await handleSubmit(mergedFormData)
  }

  useEffect(() => {
    const getForm = async () => {
      try {
        const { data } = await API.quests.getCustomForm(questId)
        const filteredQuestions = data.questions.filter((q) => !q.isDeleted) // Filter out deleted questions
        setQuestions(filteredQuestions)
        setCustomFormId(data._id) // Set the custom form ID
        setLoading(false)

        // Load saved form data from localStorage using the customFormId from API response
        const storageKey = data._id ? `${data._id}-${userId}` : null
        if (storageKey) {
          try {
            const savedData = localStorage.getItem(storageKey)
            if (savedData) {
              const parsedData = JSON.parse(savedData)
              if (Object.keys(parsedData).length > 0) {
                const sanitizedData = sanitizeFormDataAgainstQuestions(
                  parsedData,
                  filteredQuestions,
                  storageKey,
                )
                formDataRef.current = sanitizedData
                setFormData(sanitizedData)
              }
            }
          } catch (error) {
            Error.showError(error)
          }
        }
      } catch (e) {
        Error.showError(e)
        setLoading(false)
      }
    }

    if (questId) getForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId])

  // Additional useEffect to handle customFormId changes (as a backup)
  useEffect(() => {
    if (customFormId && questId && questions.length > 0) {
      const savedData = loadFormDataFromStorage()
      if (Object.keys(savedData).length > 0) {
        const sanitizedData = sanitizeFormDataAgainstQuestions(
          savedData,
          questions,
        )
        formDataRef.current = sanitizedData
        setFormData(sanitizedData)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFormId, questId])

  if (loading) {
    return (
      <div className='enroll-typeform-loading'>
        <ABSpinner animation='border' />
      </div>
    )
  }

  if (!questions.length) {
    return (
      <div className='enroll-typeform-empty'>
        <p>
          No questions available for this form. Please contact the
          administrator.
        </p>
      </div>
    )
  }

  // Get filtered questions based on dependent logic
  const filteredQuestions = getFilteredQuestions(questions, formData)

  // Find the current question in the filtered questions
  const currentQuestionInFiltered = filteredQuestions.find(
    (q) => q._id === questions[currentQuestion]?._id,
  )

  // If current question is not in filtered questions, find the first available question
  if (!currentQuestionInFiltered && filteredQuestions.length > 0) {
    const firstFilteredQuestion = filteredQuestions[0]
    const firstQuestionIndex = questions.findIndex(
      (q) => q._id === firstFilteredQuestion._id,
    )
    setCurrentQuestion(firstQuestionIndex)
  }

  // Get the current question from the original questions array
  const currentQ = questions[currentQuestion]
  const hasQuestionImage = Boolean(currentQ?.questionImageName)
  const isXLScreen =
    typeof window !== 'undefined' ? window.innerWidth >= 1200 : false
  const questionImageBreakpointClass = hasQuestionImage
    ? isXLScreen
      ? 'question-image-breakpoint-xl'
      : isLGScreen
      ? 'question-image-breakpoint-lg'
      : isMDScreen
      ? 'question-image-breakpoint-md'
      : isSMScreen
      ? 'question-image-breakpoint-sm'
      : 'question-image-breakpoint-xs'
    : ''
  const currentQuestionValue =
    formDataRef.current[currentQ._id] !== undefined
      ? formDataRef.current[currentQ._id]
      : formData[currentQ._id]

  // Determine if this is truly the last question
  // It's the last question if there are no more visible questions after the current one
  const hasMoreQuestions = hasMoreQuestionsAfterCurrent(currentQ._id, formData)
  const isLastQuestion = !hasMoreQuestions

  const currentFilteredIndex = filteredQuestions.findIndex(
    (q) => q._id === currentQ._id,
  )
  const prefetchQuestionImages =
    currentFilteredIndex < 0
      ? []
      : filteredQuestions
          .slice(currentFilteredIndex + 1, currentFilteredIndex + 5)
          .filter((q) => q.questionImageName)
          .map((q) => ({
            _id: q._id,
            questionImageName: q.questionImageName,
          }))

  return (
    <div
      className={`enroll-typeform-container${
        hasQuestionImage ? ' has-question-image' : ''
      } ${questionImageBreakpointClass}`}>
      <header
        className={`w-100 pt-12px fixed-top enroll-typeform-header ${
          hasQuestionImage ? '' : 'form-bg'
        }`}>
        <div className='container-fluid'>
          <div className='row'>
            <div className='col-12 p-0'>
              <div className='d-flex align-items-center justify-content-between pl-16px pr-16px'>
                <div>
                  <Navbar className='px-0 py-0'>
                    <Navbar.Brand
                      href={`/your-experiences?questId=${questId}`}
                      className='text-decoration-none py-0'>
                      <div className='d-flex align-items-center'>
                        <GoBackIcon className='me-2' />
                        <span className='s-captions'>
                          Return to the Abroad Platform
                        </span>
                      </div>
                    </Navbar.Brand>
                  </Navbar>
                </div>
                {/* <div>
                  <span className='font-cormorant-garamond font-weight-600 font-30px ls-4 line-height-42px'>
                    Enrollment Form
                  </span>
                </div> */}
                <div>
                  <FeatherLoader width='53' height='31' />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className='enroll-typeform-content'>
        <QuestionRenderer
          question={currentQ}
          questionIndex={
            filteredQuestions.findIndex((q) => q._id === currentQ._id) + 1
          }
          totalQuestions={filteredQuestions.length}
          value={currentQuestionValue}
          onSubmit={(answer) => handleQuestionSubmit(currentQ._id, answer)}
          onSaveAnswer={(answer) => handleSaveAnswer(currentQ._id, answer)}
          onNext={handleNext}
          onPrevious={handlePrevious}
          isLastQuestion={isLastQuestion}
          submitting={submitting}
          onFinalSubmit={handleFinalSubmit}
          formData={formData}
          questId={questId}
          customFormId={customFormId}
          allQuestions={questions} // Pass all questions for dependent logic
          hasMoreQuestionsAfterCurrent={hasMoreQuestionsAfterCurrent} // Pass the helper function
          prefetchQuestionImages={prefetchQuestionImages}
          questsApi={API.quests}
        />
      </div>
    </div>
  )
}

export default EnrollTypeForm
